/**
 * Contact API input schema, normalization, validation, and canonical payload hashing for Spaq Inc.
 */

export const PRIVACY_VERSION = "spaq-contact-2026-11-01";
export const MAX_BODY_BYTES = 32 * 1024; // 32 KiB

export const CATEGORIES = [
  "facility",
  "1minute-solar",
  "spaq-core",
  "partner",
  "other",
] as const;
export type ContactCategory = (typeof CATEGORIES)[number];

export const CATEGORY_LABELS: Record<ContactCategory, string> = {
  facility: "自社施設の電力・設備について（電気代削減・設備設計・施工など）",
  "1minute-solar": "1minute solar・太陽光の正式検討・個別試算について",
  "spaq-core": "SPAQ CORE（太陽光・空調・蓄電池の協調制御）について",
  partner: "協業・販売・施工連携・共同検討について",
  other: "その他のお問い合わせ（イルミネーション事業・取材等）",
};

export interface NormalizedContactInput {
  schemaVersion: 1;
  category: ContactCategory;
  organization: string;
  name: string;
  email: string;
  phone: string | null;
  facilityInfo: string | null;
  message: string;
  privacyAccepted: true;
  privacyVersion: string;
  turnstileToken: string;
}

export type ValidationResult =
  | {
      ok: true;
      data: NormalizedContactInput;
    }
  | {
      ok: false;
      status: 400 | 422;
      code: "invalid_request" | "validation_error";
      message: string;
      fieldErrors?: Record<string, string>;
    };

const ALLOWED_KEYS = new Set([
  "schemaVersion",
  "category",
  "organization",
  "name",
  "email",
  "phone",
  "facilityInfo",
  "message",
  "privacyAccepted",
  "privacyVersion",
  "turnstileToken",
  "website",
]);

export function codePointLength(str: string): number {
  return Array.from(str).length;
}

export function hasCrLf(str: string): boolean {
  return /[\r\n\0]/.test(str);
}

export function isValidEmail(email: string): boolean {
  if (!email || codePointLength(email) > 254 || email.length > 254) {
    return false;
  }
  if (hasCrLf(email) || /\s/.test(email)) {
    return false;
  }
  const atIndex = email.lastIndexOf("@");
  if (atIndex <= 0 || atIndex !== email.indexOf("@") || atIndex === email.length - 1) {
    return false;
  }
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);

  if (codePointLength(local) > 64 || local.length > 64) {
    return false;
  }
  if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) {
    return false;
  }
  if (!/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(local)) {
    return false;
  }

  // Domain checks
  if (domain.startsWith(".") || domain.endsWith(".") || domain.includes("..")) {
    return false;
  }
  const labels = domain.split(".");
  if (labels.length < 2) {
    return false;
  }
  for (const label of labels) {
    if (!label || label.length > 63) return false;
    if (label.startsWith("-") || label.endsWith("-")) return false;
    if (!/^[A-Za-z0-9-]+$/.test(label)) return false;
  }
  const tld = labels[labels.length - 1];
  if (/^\d+$/.test(tld) || tld.length < 2) {
    return false;
  }

  return true;
}

export function normalizeEmail(raw: string): string {
  const trimmed = raw.trim();
  const atIndex = trimmed.lastIndexOf("@");
  if (atIndex <= 0 || atIndex === trimmed.length - 1) {
    return trimmed;
  }
  const local = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1).toLowerCase();
  return `${local}@${domain}`;
}

export function isValidIdempotencyKey(key: string): boolean {
  if (!key || typeof key !== "string") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    key.trim()
  );
}

export function validateContactPayload(rawBody: unknown): ValidationResult {
  if (typeof rawBody !== "object" || rawBody === null || Array.isArray(rawBody)) {
    return {
      ok: false,
      status: 400,
      code: "invalid_request",
      message: "リクエスト形式が不正です。JSONオブジェクトを送信してください。",
    };
  }

  const record = rawBody as Record<string, unknown>;

  // Check for unknown keys
  for (const key of Object.keys(record)) {
    if (!ALLOWED_KEYS.has(key)) {
      return {
        ok: false,
        status: 400,
        code: "invalid_request",
        message: `未知の項目が含まれています: ${key}`,
      };
    }
  }

  // Honeypot check: website must be empty
  if ("website" in record && record.website !== undefined && record.website !== null && record.website !== "") {
    return {
      ok: false,
      status: 422,
      code: "validation_error",
      message: "送信内容に不正な値が含まれています。",
    };
  }

  const fieldErrors: Record<string, string> = {};

  // schemaVersion
  const schemaVersion = record.schemaVersion;
  if (schemaVersion !== undefined && schemaVersion !== 1) {
    fieldErrors.schemaVersion = "未対応のスキーマバージョンです。";
  }

  // category
  const categoryRaw = record.category;
  let category: ContactCategory = "facility";
  if (typeof categoryRaw !== "string" || !CATEGORIES.includes(categoryRaw as ContactCategory)) {
    fieldErrors.category = "ご相談種別を正しく選択してください。";
  } else {
    category = categoryRaw as ContactCategory;
  }

  // organization (必須)
  const orgRaw = record.organization;
  let organization = "";
  if (typeof orgRaw !== "string" || !orgRaw.trim()) {
    fieldErrors.organization = "貴社名・団体名を入力してください。";
  } else {
    organization = orgRaw.trim();
    if (codePointLength(organization) > 100) {
      fieldErrors.organization = "貴社名・団体名は100文字以内で入力してください。";
    }
  }

  // name (必須)
  const nameRaw = record.name;
  let name = "";
  if (typeof nameRaw !== "string" || !nameRaw.trim()) {
    fieldErrors.name = "ご担当者名を入力してください。";
  } else {
    name = nameRaw.trim();
    if (codePointLength(name) > 60) {
      fieldErrors.name = "ご担当者名は60文字以内で入力してください。";
    }
    if (hasCrLf(name)) {
      fieldErrors.name = "ご担当者名に改行文字を含めることはできません。";
    }
  }

  // email (必須)
  const emailRaw = record.email;
  let email = "";
  if (typeof emailRaw !== "string" || !emailRaw.trim()) {
    fieldErrors.email = "メールアドレスを入力してください。";
  } else {
    const candidate = normalizeEmail(emailRaw);
    if (!isValidEmail(candidate)) {
      fieldErrors.email = "メールアドレスの形式が正しくありません。";
    } else {
      email = candidate;
    }
  }

  // phone (任意)
  const phoneRaw = record.phone;
  let phone: string | null = null;
  if (phoneRaw !== undefined && phoneRaw !== null && phoneRaw !== "") {
    if (typeof phoneRaw !== "string") {
      fieldErrors.phone = "お電話番号の形式が正しくありません。";
    } else {
      const trimmed = phoneRaw.trim();
      if (trimmed) {
        if (codePointLength(trimmed) > 30) {
          fieldErrors.phone = "お電話番号は30文字以内で入力してください。";
        } else if (!/^[0-9+\-() ]+$/.test(trimmed)) {
          fieldErrors.phone = "お電話番号に使用できない文字が含まれています。";
        } else {
          phone = trimmed;
        }
      }
    }
  }

  // facilityInfo (任意: 対象施設の種別・所在地エリア)
  const facilityInfoRaw = record.facilityInfo;
  let facilityInfo: string | null = null;
  if (facilityInfoRaw !== undefined && facilityInfoRaw !== null && facilityInfoRaw !== "") {
    if (typeof facilityInfoRaw !== "string") {
      fieldErrors.facilityInfo = "対象施設・エリア情報の形式が正しくありません。";
    } else {
      const trimmed = facilityInfoRaw.trim();
      if (trimmed) {
        if (codePointLength(trimmed) > 200) {
          fieldErrors.facilityInfo = "対象施設・エリア情報は200文字以内で入力してください。";
        } else {
          facilityInfo = trimmed;
        }
      }
    }
  }

  // message (必須)
  const messageRaw = record.message;
  let message = "";
  if (typeof messageRaw !== "string" || !messageRaw.trim()) {
    fieldErrors.message = "お問い合わせ内容を入力してください。";
  } else {
    message = messageRaw.trim();
    if (codePointLength(message) > 3000) {
      fieldErrors.message = "お問い合わせ内容は3,000文字以内で入力してください。";
    }
  }

  // privacyAccepted (必須: true)
  const privacyAcceptedRaw = record.privacyAccepted;
  if (privacyAcceptedRaw !== true) {
    fieldErrors.privacyAccepted = "プライバシーポリシーへの同意が必要です。";
  }

  // privacyVersion (必須)
  const privacyVersionRaw = record.privacyVersion;
  let privacyVersion = PRIVACY_VERSION;
  if (typeof privacyVersionRaw !== "string" || !privacyVersionRaw.trim()) {
    fieldErrors.privacyVersion = "プライバシーポリシーのバージョン情報が必要です。";
  } else {
    privacyVersion = privacyVersionRaw.trim();
  }

  // turnstileToken (必須)
  const turnstileTokenRaw = record.turnstileToken;
  let turnstileToken = "";
  if (typeof turnstileTokenRaw !== "string" || !turnstileTokenRaw.trim()) {
    fieldErrors.turnstileToken = "セキュリティ認証（Turnstile）が未完了です。";
  } else {
    turnstileToken = turnstileTokenRaw.trim();
  }

  if (Object.keys(fieldErrors).length > 0) {
    return {
      ok: false,
      status: 422,
      code: "validation_error",
      message: "入力内容に不備があります。各項目のエラーをご確認ください。",
      fieldErrors,
    };
  }

  return {
    ok: true,
    data: {
      schemaVersion: 1,
      category,
      organization,
      name,
      email,
      phone,
      facilityInfo,
      message,
      privacyAccepted: true,
      privacyVersion,
      turnstileToken,
    },
  };
}

/**
 * Computes canonical SHA-256 hash of normalized payload (excluding volatile fields like turnstileToken).
 */
export async function computePayloadHash(
  payload: Omit<NormalizedContactInput, "turnstileToken">
): Promise<string> {
  const canonicalObj = {
    schemaVersion: 1,
    category: payload.category,
    organization: payload.organization,
    name: payload.name,
    email: payload.email,
    phone: payload.phone ?? "",
    facilityInfo: payload.facilityInfo ?? "",
    message: payload.message,
    privacyAccepted: true,
    privacyVersion: payload.privacyVersion,
  };

  const canonicalJson = JSON.stringify(canonicalObj);
  const data = new TextEncoder().encode(canonicalJson);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
